import { CaseModel } from 'data/case/case.types';
import {
  Label, Button, Classes, Dialog,
} from '@blueprintjs/core';
import i18n from 'i18n';
import { FormTemplate, FormTemplateOption, FormTemplateSection } from 'data/template/template.types';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FormPanel from 'components/FormPanel';

import { DeviceModelMap, DeviceModel } from 'xcipio/devices/device.types';
import { TargetModelValidation } from 'data/target/target.types';
import DynamicForm, { ChangedField } from '../../../../../shared/form';
import template from '../../template/caseInfoConfirmationTemplate';
import './confirmation-form.scss';
import TargetValue from '../TargetValue';
import ConfirmationActions from '../ConfirmationActions';
import { setTemplateInitialValues } from '../../utils/casesInfoConfirmationTemplate.adapter';

interface CasesConfirmationFormProps {
  warrantTemplate: FormTemplate
  currentCase: CaseModel
  caseNumber: number
  countCasesToConfirm: number
  devicesToConfirm: DeviceModelMap
  isNokamflcsimEnabled?: boolean
  isDisplayLocDataInInterceptDropDownEnabled?: boolean
  onClose: () => void
  onSubmit: () => void
  onNext: () => void
  onBack: () => void
}

export interface TargetsMap {
  [primaryType: string]: boolean
}

const CasesConfirmationForm = ({
  warrantTemplate,
  currentCase,
  caseNumber,
  countCasesToConfirm,
  devicesToConfirm,
  isNokamflcsimEnabled = false,
  isDisplayLocDataInInterceptDropDownEnabled = false,
  onClose,
  onSubmit,
  onNext,
  onBack,
}: CasesConfirmationFormProps): JSX.Element => {
  const { t } = useTranslation();
  const [isValid, setIsValid] = useState(false);
  const [cfIdvalid, setCfIdValid] = useState(false);
  const [interceptTypeValid, setInterceptTypeValid] = useState(false);
  const [targetsValidMap, setTargetsValidMap] = useState<TargetsMap>({});
  const [targetsToValidate, setTargetsToValidate] = useState<TargetModelValidation[]>([]);

  useEffect(() => {
    setCfIdValid(false);
    setInterceptTypeValid(false);
  }, [caseNumber])

  useEffect(() => {
    let current: DeviceModel = {
      device: '',
      services: [],
      allTargetAfs: [],
      types: {}
    };
    setTargetsToValidate([]);
    const targets: TargetModelValidation[] = [];
    Object.keys(devicesToConfirm).forEach((key, index) => {
      if (devicesToConfirm[key].caseIds?.includes(currentCase.id)) {
        current = { ...devicesToConfirm[key] }
      }
    })

    if (current) {
      Object.keys(current?.types).forEach((key, index) => {
        const target = {
          primaryType: current?.types[key][0].target.primaryType,
          primaryValue: current?.types[key][0].target.primaryValue,
          isRequired: current?.types[key][0].target.isRequired,
          targetId: current?.types[key][0].target.id,
          isSynthesized: current?.types[key][0].target.synthesized,
          display: current?.types[key][0].display,
        };

        if (target.primaryValue && target.primaryValue !== '' && target.display) {
          if (target.primaryType === 'PARTIAL_DN') {
            target.primaryType = 'Partial DN';
          }
          if (target.primaryType === 'MDNCustom') {
            target.primaryType = 'MDN';
          }
          targets.push(target);
        }
      })
    }

    const targetsMap = targets.reduce((acc: TargetsMap, target: TargetModelValidation) =>
      ({ ...acc, [target.primaryType as string]: target.primaryValue === '' }), {});
    setTargetsValidMap(targetsMap);
    setTargetsToValidate(targets);
  }, [currentCase]);

  useEffect(() => {
    setIsValid(Object.keys(targetsValidMap).every((key) => targetsValidMap[key] === true));
  }, [targetsValidMap]);

  const handleCfAndTypeUpdate = ({ name, value }: ChangedField) => {
    if (name === 'confirmCfId') {
      if (value === currentCase.cfId.id) {
        setCfIdValid(true);
      } else {
        setCfIdValid(false);
      }
    }

    if (name === 'confirmType') {
      if (value === currentCase.type) {
        setInterceptTypeValid(true);
      } else {
        setInterceptTypeValid(false);
      }
    }
  };

  return (
    <div className="confirmationFormContainer" data-testid="casesConfirmationForm">
      <p>{t('warrant.create.targetInfoConfirmationDescription')}</p>
      <h3>Case {caseNumber}</h3>
      <div className="fixedFormContainer">
        <div className="formField">
          <DynamicForm
            key={caseNumber}
            fields={setTemplateInitialValues(warrantTemplate, currentCase, 'cfId')}
            name={template.key}
            layout={(template.cfId as FormTemplateSection).metadata.layout?.rows}
            i18nKeyPrefix="confirmation.form"
            fieldUpdateHandler={handleCfAndTypeUpdate}
          />
          {!cfIdvalid && (
            <Label className="field-error-message">
              {i18n.t('warrant.target.form.targetIdValidationError')}
            </Label>
          )}
        </div>
        <div className="formField">
          <DynamicForm
            key={caseNumber}
            fields={setTemplateInitialValues(warrantTemplate, currentCase, 'interceptType', isNokamflcsimEnabled, isDisplayLocDataInInterceptDropDownEnabled)}
            name={template.key}
            layout={(template.interceptType as FormTemplateSection).metadata.layout?.rows}
            i18nKeyPrefix="confirmation.form"
            fieldUpdateHandler={handleCfAndTypeUpdate}
          />
          {!interceptTypeValid && (
            <Label className="field-error-message">
              {i18n.t('warrant.target.form.targetIdValidationError')}
            </Label>
          )}
        </div>
      </div>
      <h3>Targets</h3>
      <div className="dynamicFormContainer">
        {targetsToValidate.map((target: TargetModelValidation) => {
          return (
            <TargetValue
              key={target.primaryType}
              primaryType={target.primaryType as string}
              primaryValue={target.primaryValue as string}
              targetsMap={targetsValidMap}
              isRequired={target.isRequired}
              setIsValid={setTargetsValidMap}
              caseNumber={caseNumber}
            />
          );
        })}
      </div>
      <ConfirmationActions
        currentStep={caseNumber}
        totalSteps={countCasesToConfirm}
        isValid={isValid && cfIdvalid && interceptTypeValid}
        onClose={onClose}
        onSubmit={onSubmit}
        onNext={onNext}
        onBack={onBack}
      />
    </div>
  );
};

export default CasesConfirmationForm;
